// D11's rest timer, as data: a stored END time, never a counter, so a
// reload, a locked phone or a backgrounded tab still shows the right time
// left — remaining is always `endsAt − now`. Persisted per person at
// `mono.training.<userId>.rest` (a refused write keeps it in memory for the
// visit). useRestTimer.js drives the display from this.
import { readStored, trainingKey, writeStored } from "./storage";

// D11 (R§3.5): heavy main sets 4 min, other main 3, accessories 2,
// shoulder health 75 s. The API has no rest field; these are the spec's.
export const REST_SECONDS = {
  heavyMain: 240,
  main: 180,
  accessory: 120,
  shoulderHealth: 75,
};
// "+30 s" on the rest bar.
export const EXTEND_SECONDS = 30;
// A rest that ended this long ago is history, not "rest over — next set".
export const STALE_AFTER_MS = 10 * 60 * 1000;

/**
 * The rest after a set of this plan item: by its role (or the exercise's,
 * for an item that doesn't say) and, for a main lift, its day type. An
 * item with no role at all rests like an accessory.
 */
export function restSeconds(item, exercise) {
  const role = item?.role ?? exercise?.role;
  if (role === "shoulder_health") return REST_SECONDS.shoulderHealth;
  if (role === "main") {
    return item?.day_type === "heavy" ? REST_SECONDS.heavyMain : REST_SECONDS.main;
  }
  return REST_SECONDS.accessory;
}

export const restKey = (userId) => trainingKey(userId, "rest");

/** A stored rest worth reading: `{endsAt, seconds, sessionId?, exercise?}`. */
function isRest(value) {
  return (
    value !== null &&
    typeof value === "object" &&
    Number.isFinite(value.endsAt) &&
    Number.isFinite(value.seconds) &&
    value.seconds > 0
  );
}

/**
 * Where one person's rest lives. `read()` → the rest or null (junk, or
 * one that ended over STALE_AFTER_MS ago, reads as none); `write(rest)` →
 * true when it reached localStorage; `clear()`.
 */
export function createRestStore(userId) {
  const key = restKey(userId);
  return {
    key,
    read(now = Date.now()) {
      const value = readStored(key);
      if (!isRest(value)) return null;
      return now - value.endsAt > STALE_AFTER_MS ? null : value;
    },
    write(rest) {
      return writeStored(key, rest);
    },
    clear() {
      return writeStored(key, null);
    },
  };
}

/** Start a rest of `seconds` now; persisted when `store` is given. */
export function startRest(store, { seconds, sessionId, exercise } = {}, now = Date.now()) {
  const rest = {
    endsAt: now + seconds * 1000,
    seconds,
    ...(sessionId !== undefined ? { sessionId } : {}),
    ...(exercise !== undefined ? { exercise } : {}),
  };
  store?.write(rest);
  return rest;
}

/**
 * `by` more seconds: from the end while it runs, from now once it's over
 * (so "+30 s" after the bell is 30 more seconds, not time already gone).
 */
export function extendRest(store, rest, by = EXTEND_SECONDS, now = Date.now()) {
  if (!rest) return null;
  const endsAt = Math.max(rest.endsAt, now) + by * 1000;
  const next = { ...rest, endsAt, seconds: rest.seconds + by };
  store?.write(next);
  return next;
}

/** Stop resting now. */
export function skipRest(store) {
  store?.clear();
  return null;
}

/** Whole seconds left (rounded up: "0:01" until the very end), never below 0. */
export function remainingSeconds(rest, now = Date.now()) {
  if (!rest) return 0;
  return Math.max(0, Math.ceil((rest.endsAt - now) / 1000));
}

/** 240 → "4:00", 75 → "1:15", 9 → "0:09". */
export function formatClock(seconds) {
  const s = Math.max(0, Math.round(seconds));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;
}
